import { ReactNode, useContext, useMemo } from "react";
import classNames from "classnames";
import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { TabbedContainer, TabbedContainerProps } from "../TabbedContainer";
import { PanelAppearance } from "./PanelAppearance";
import { PanelContext } from "./PanelContext";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

const useResolvedPanelStyles = createComponentStyleHook<PanelProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "panel",
});

/** The `Panel` component properties. */
export interface PanelProps extends BaseProps {
  /** Specifies the panel appearance. If not specified defaults to "auto". */
  readonly appearance?: PanelAppearance;

  /** The panel aria role. */
  readonly role?: string;

  /** The component children. */
  readonly children?: ReactNode;
}

/**
 * Gets the level of the currently containing panel.
 * @returns The containing panel level.
 */
export function usePanelLevel(): "none" | Omit<PanelAppearance, "auto"> {
  const parentPanelContext = useContext(PanelContext);

  if (parentPanelContext.level === -1) return "none";
  if (parentPanelContext.level === 0) return "primary";

  return "secondary";
}

/** A simple container panel. */
export const Panel = (props: PanelProps) => {
  const { children, role } = props;
  let { appearance = "auto" } = props;

  const parentPanelLevel = usePanelLevel();
  const { mode, styles, StyleModeBoundary } = useResolvedPanelStyles(props);

  if (appearance === "auto") {
    if (parentPanelLevel === "none") appearance = "primary";
    else appearance = "secondary";
  }

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      classNames(styles.Panel, "panel", {
        [styles.Primary]: appearance === "primary",
        [styles.Secondary]: appearance === "secondary",
      }),
    ],
    testIdPrefix: "panel",
  });

  const contextData = useMemo(() => {
    if (appearance === "primary") return { level: 0 };

    return { level: 1 };
  }, [appearance]);

  return (
    <StyleModeBoundary mode={mode}>
      <PanelContext.Provider value={contextData}>
        <div role={role} {...baseHtmlProps}>
          {children}
        </div>
      </PanelContext.Provider>
    </StyleModeBoundary>
  );
};

/** A variant of the `Panel` component which has tabs. */
const TabbedPanel = (props: TabbedContainerProps & PanelProps) => {
  const { className, testId, style, appearance: appearanceProp = "auto", role, children, ...tabbedContainerProps } = props;
  const parentPanelLevel = usePanelLevel();
  const { mode, styles, StyleModeBoundary } = useResolvedPanelStyles(props);

  let appearance = appearanceProp;
  if (appearance === "auto") {
    if (parentPanelLevel === "none") appearance = "primary";
    else appearance = "secondary";
  }

  const baseHtmlProps = getHtmlPropsFromBaseProps(
    { className, testId, style },
    {
      className: [
        classNames(styles.Panel, styles.TabbedPanel, "panel", "tabbed-panel", {
          [styles.Primary]: appearance === "primary",
          [styles.Secondary]: appearance === "secondary",
        }),
      ],
      testIdPrefix: "panel",
    },
  );

  const contextData = useMemo(() => {
    if (appearance === "primary") return { level: 0 };
    return { level: 1 };
  }, [appearance]);

  return (
    <StyleModeBoundary mode={mode}>
      <PanelContext.Provider value={contextData}>
        <div role={role} {...baseHtmlProps}>
          <TabbedContainer className={styles.TabbedContainer} {...tabbedContainerProps}>
            {children}
          </TabbedContainer>
        </div>
      </PanelContext.Provider>
    </StyleModeBoundary>
  );
};

/** An individual pane in the tabbed panel. */
TabbedPanel.TabPane = TabbedContainer.TabPane;

/** A variant of the `Panel` component which has tabs. */
Panel.Tabbed = TabbedPanel;
